import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { getCityBySlug } from '../data/cities';

const CONTENT_BASE = import.meta.env.VITE_CONTENT_BASE_URL
  || 'https://content.helpped.com';

const TESTFLIGHT_URL = 'https://testflight.apple.com/join/helpped';

interface ResourceItem {
  name: string;
  slug: string;
  category: string | null;
  summary: string | null;
  address: string | null;
  phone: string | null;
  hours: string | null;
}

interface ResourceIndex {
  city_slug: string;
  count: number;
  resources: ResourceItem[];
}

const CATEGORY_LABELS: Record<string, string> = {
  food: 'Food Assistance',
  shelter: 'Shelters & Emergency Housing',
  housing: 'Housing Assistance',
  health: 'Health & Medical',
  behavioral_health: 'Mental & Behavioral Health',
  legal: 'Legal Aid',
  seniors: 'Senior Services',
  youth: 'Youth Services',
  social_services: 'Social Services',
  other: 'Other Resources',
};

function slugToName(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default function ResourcePage() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const city = getCityBySlug(citySlug ?? '');
  const cityName = city?.name ?? slugToName(citySlug ?? '');

  const [data, setData] = useState<ResourceIndex | null>(null);
  const [loading, setLoading] = useState(true);

  usePageMeta(
    `Community Resources in ${cityName}, WA — Food, Shelter & Help | Helpped`,
    `Find food banks, shelters, free clinics, and social services in ${cityName}, WA. Hours, eligibility, and how to get help — updated regularly.`,
    city ? { placename: `${city.name}, Washington`, lat: city.lat, lon: city.lon } : undefined,
  );

  useEffect(() => {
    if (!citySlug) return;
    fetch(`${CONTENT_BASE}/resources/${citySlug}.json`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [citySlug]);

  if (!citySlug) return null;

  const grouped: Record<string, ResourceItem[]> = {};
  (data?.resources ?? []).forEach((r) => {
    const cat = r.category || 'other';
    (grouped[cat] ??= []).push(r);
  });

  const itemListSchema = data && data.resources.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Community Resources in ${cityName}, WA`,
    numberOfItems: data.count,
    itemListElement: data.resources.slice(0, 50).map((r, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'LocalBusiness',
        name: r.name,
        address: r.address || undefined,
        telephone: r.phone || undefined,
      },
    })),
  } : null;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {itemListSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
      )}

      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center gap-2 text-sm">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background: 'hsl(var(--primary))' }}>H</div>
            <span className="font-bold text-gray-900">Helpped</span>
          </Link>
          <span className="text-gray-300 mx-1">/</span>
          {city
            ? <Link to={`/cities/${citySlug}`} className="text-gray-500 hover:text-gray-800">{cityName}</Link>
            : <span className="text-gray-500">{cityName}</span>}
          <span className="text-gray-300 mx-1">/</span>
          <span className="text-gray-700 font-medium">Resources</span>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10 w-full flex-1">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-1">
          Community Resources in {cityName}, WA
        </h1>
        <p className="text-sm text-gray-500 mb-8">
          Food assistance, shelters, clinics, and social services — with hours,
          eligibility, and how to get help.
        </p>

        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : !data || data.resources.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <p className="text-3xl mb-3">🤝</p>
            <p className="text-sm">Resource listings for {cityName} are being compiled — check back soon.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([cat, items]) => (
              <section key={cat}>
                <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                  {CATEGORY_LABELS[cat] || cat}
                </h2>
                <div className="space-y-3">
                  {items.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/cities/${citySlug}/org/${r.slug}`}
                      className="block p-4 rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow group"
                    >
                      <h3 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                        {r.name}
                      </h3>
                      {r.summary && (
                        <p className="text-sm text-gray-500 leading-relaxed mt-1 line-clamp-2">{r.summary}</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-400">
                        {r.address && <span>{r.address}</span>}
                        {r.phone && <span>{r.phone}</span>}
                        {r.hours && <span>{r.hours}</span>}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 p-5 rounded-2xl border border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <p className="text-sm text-gray-700 flex-1">
            Get {cityName} resources, civic news, and neighborhood updates on your phone — free on iOS.
          </p>
          <a href={TESTFLIGHT_URL} target="_blank" rel="noopener noreferrer"
            className="shrink-0 px-5 py-2.5 rounded-xl font-semibold text-white text-sm hover:opacity-90 transition-opacity"
            style={{ background: '#000' }}>
            Get the app
          </a>
        </div>
      </main>

      <footer className="px-6 py-6 border-t border-gray-100">
        <div className="max-w-3xl mx-auto flex items-center justify-between text-xs text-gray-400">
          <Link to="/" className="hover:text-gray-600">← Helpped.com</Link>
        </div>
      </footer>
    </div>
  );
}
